import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import {
    CategoryModel,
    ConfigConstants,
    InternalInfoModel,
    InternalInfoMultilineTextModel,
    IValueChanged,
    TextInputComponent,
} from '@evorait/pace-core';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { Subject, timer } from 'rxjs';

@Component({
    selector: 'pace-internal-info-category',
    templateUrl: './internal-info-category.component.html',
    styleUrls: ['./internal-info-category.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InternalInfoCategoryComponent implements OnInit, OnDestroy {
    @Input()
    public model: InternalInfoModel;

    @Input()
    public parentForm: FormArray;

    @Input()
    public categoryType: ConfigConstants.INTERNAL_INFO_CATEGORY;

    @Input()
    public categoryModel: CategoryModel;

    @ViewChild('categoryInputElement')
    public categoryInputElement: TextInputComponent;

    private destroy$ = new Subject<void>();
    public displayCategoryText: InternalInfoMultilineTextModel;
    public displayCategoryTextLines: string[];
    public formattedTextLines: string[];
    public reverse: string | boolean = '';
    public lastEditedByLabel: string;
    public categoryForm: FormGroup;
    public categoryFormChildren: FormArray;
    public constructor(
        private readonly translate: TranslateService,
        private readonly formBuilder: FormBuilder,
    ) {
        this.lastEditedByLabel = <string>this.translate.instant('INTERNAL_INFO_LAST_EDITED_BY');
    }

    public ngOnInit(): void {
        this.categoryFormChildren = this.formBuilder.array([]);
        this.categoryForm = this.formBuilder.group({
            children: this.categoryFormChildren,
        });

        this.parentForm.push(this.categoryForm);
        this.categoryModel.textLine.value = this.categoryModel.textLine.getTextToBeDisplayedInTextArea();
        this.displayCategoryText = this.categoryModel.textLine;
        this.updateTextLines();
    }

    public updateTextLines(): void {
        // reset reverse indicator to '', so that no icon is displayed
        this.reverse = '';

        const categoryText = this.displayCategoryText.value.split('\n');
        this.formattedTextLines = categoryText.reduce(
            (textLines: string[], message, index) => {
                textLines[textLines.length - 1] += `${message}\n`;
                // if string matches 'dd.mm.yyyy by <gid>' pattern and it is not the last array element,
                // then push new element to the textLines[] array.
                if (
                    message.match(ConfigConstants.internalInfoTextLineRegex.headerInfo) &&
                    index !== categoryText.length - 1
                ) {
                    textLines.push('');
                }
                return textLines;
            },
            [''],
        );

        // To display message by each line, we need to join the formattedTextLines array first and then split by '\n' character
        this.displayCategoryTextLines = this.formattedTextLines.join('').split('\n');
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
    }

    public onValueChange(event: IValueChanged): void {
        if (event.oldValue !== event.newValue) {
            this.categoryModel.isEdited = true;
            this.categoryModel.isContentAvailable = !!this.categoryModel.textLine.value.length;
            this.categoryModel.isInvalid = this.categoryForm.invalid;
        }
    }

    public onCancel(): void {
        this.categoryModel.isEdited = false;

        this.model.restoreOriginalCategoryValuesForSelectedCategory(this.categoryType);
        this.displayInformation();
    }

    public onEdit(): void {
        this.categoryModel.isEdited = true;

        const focusField = <HTMLInputElement>this.categoryInputElement.internalInfoTextAreaInput.nativeElement;

        // For categories like Parking, dispatch, subsystem etc cursor pointer should be focused on new line
        // If new line is already updated then concatenation '\n' is not required
        if (focusField.value && !focusField.value.endsWith('\n')) {
            focusField.value += '\n';
        }

        //  Focus on last character of the string
        const charPosition = focusField.value.length;

        timer(1).subscribe(() => {
            focusField.setSelectionRange(charPosition, charPosition);
            focusField.focus();
        });
    }

    public displayInformation(): void {
        const originalCategoryData = this.model.getCategoryValues(this.categoryType);
        originalCategoryData.textLine.value = originalCategoryData.textLine.getTextToBeDisplayedInTextArea();

        this.displayCategoryText = originalCategoryData.textLine;
        this.updateTextLines();
    }

    public sortTextByDate(): void {
        if (this.reverse === '') {
            // if user is clicking sort button for the first time after updating the category,
            // then set the reverse order to false (ascending order)
            this.reverse = false;
        } else {
            this.reverse = !this.reverse;
        }
        const sortedArray = this.formattedTextLines.sort((message1, message2) => {
            // find 'dd.mm.yyyy by <gid>' pattern from the string
            const dateByGid1 = message1.match(ConfigConstants.internalInfoTextLineRegex.headerInfo);
            const dateByGid2 = message2.match(ConfigConstants.internalInfoTextLineRegex.headerInfo);
            const ten = 10;
            if (dateByGid1 === null) {
                return 1;
            } else if (dateByGid2 === null) {
                return -1;
            }
            // Compare and sort the dates from the matched string. index 0-9 will contain the date - DD.MM.YYYY
            const date1 = moment(dateByGid1[0].substr(0, ten), 'DD.MM.YYYY');
            const date2 = moment(dateByGid2[0].substr(0, ten), 'DD.MM.YYYY');
            return date1.diff(date2);
        });

        // sort in ascending or descending order based on the reverse indicator value.
        // reverse = false -> ascending order
        // reverse = true -> descending order
        this.displayCategoryTextLines = this.reverse
            ? sortedArray.reverse().join('').split('\n')
            : sortedArray.join('').split('\n');
    }
}
